import React from 'react';
import {useDispatch, useSelector} from 'react-redux';

import Banner from '@/components/Banner/Banner';
import {AppButton} from '@/components/Button';
import PromotedBadge from '@/components/PromotedBadge/PromotedBadge';
import appConfig from '@/config/appConfig.typed';
import {PUSH_NOTIFICATIONS_PROMPT_TYPES} from '@/consts/pushNotifications/pushNotificationsPromptTypes';
import useIsMobileLayout from '@/hooks/layout/useIsMobileLayout';
import {getAriaLabel} from '@/services/accessibilityService';
import amplitudeAutoDeliveryTracking from '@/services/analytics/amplitude/amplitudeAutoDeliveryTracking';
import googleAnalyticsService from '@/services/analytics/ga/googleAnalyticsService';
import {getLocalizedStrings} from '@/services/localization/localizationService';
import {schedulePushPromptIfNeeded} from '@/services/pushNotification/pushNotificationPromptService';
import {selectIsAem, selectIsMobileLayout} from '@/state/selectors/global';
import {showAutoDeliveryStatusNoneDetailsPopup} from '@/state/slices/modalSlice';

import AutoDeliveryWidgetContainer from '../components/AutoDeliveryWidgetContainer';
import AutoDeliveryWidgetHeader from '../components/AutoDeliveryWidgetHeader';

const AutoDeliveryStatusNone = ({className}) => {
    const dispatch = useDispatch();
    const isMobileLayout = useSelector(selectIsMobileLayout);
    const banner = appConfig.getHomePageAutoDeliveryBanner();
    const {
        imageMedia,
        videoPath,
        isGradient,
        titleKey,
        subTitleKey,
        descriptionKey,
        buttonActionKey,
        actionAriaLabelKey,
    } = banner || {};
    const isAem = useSelector(selectIsAem);
    const isBannerDataExist = imageMedia || videoPath || titleKey || subTitleKey || descriptionKey;
    const ariaLabel = getAriaLabel(actionAriaLabelKey);

    const onBannerClick = () => {
        schedulePushPromptIfNeeded(PUSH_NOTIFICATIONS_PROMPT_TYPES.AUTODELIVERY_DASHBOARD_CLICK);
        amplitudeAutoDeliveryTracking.trackAutoDeliveryBannerClick();
        googleAnalyticsService.trackAutoDeliveryBannerClick();
        dispatch(showAutoDeliveryStatusNoneDetailsPopup());
    };

    if (!isBannerDataExist && !isAem) return;

    return (
        <AutoDeliveryWidgetContainer className={className}>
            {isMobileLayout && (
                <>
                    <AutoDeliveryWidgetHeader />
                    <Banner
                        className='mt-4 h-60 overflow-hidden rounded-xl bg-primary text-start'
                        imageMedia={imageMedia}
                        videoPath={videoPath}
                        isGradient={isGradient}
                        gradient='linear-gradient(180deg, rgba(52, 48, 61, 0) 39.99%, #1C1A21 100%)'
                        aria-label={ariaLabel}
                        onClick={onBannerClick}
                    >
                        <AutoDeliveryStatusNoneContent
                            titleKey={titleKey}
                            subTitleKey={subTitleKey}
                            descriptionKey={descriptionKey}
                        />
                    </Banner>
                </>
            )}
            {!isMobileLayout && (
                <div className='flex w-full items-center rounded-xl bg-primary-dark'>
                    <AutoDeliveryStatusNoneContent
                        titleKey={titleKey}
                        subTitleKey={subTitleKey}
                        descriptionKey={descriptionKey}
                        buttonActionKey={buttonActionKey}
                        aria-label={ariaLabel}
                        onClick={onBannerClick}
                    />
                    <Banner
                        className='h-100 w-1/2 overflow-hidden rounded-r-xl'
                        imageMedia={imageMedia}
                        videoPath={videoPath}
                        isGradient={isGradient}
                    />
                </div>
            )}
        </AutoDeliveryWidgetContainer>
    );
};

const AutoDeliveryStatusNoneContent = ({
    titleKey,
    subTitleKey,
    descriptionKey,
    buttonActionKey,
    'aria-label': ariaLabel,
    onClick,
}) => {
    const isMobile = useIsMobileLayout();
    const localizedStrings = getLocalizedStrings();
    const description = localizedStrings[descriptionKey];
    const Heading = isMobile ? 'h3' : 'h2';

    return (
        <div className='relative flex h-full flex-col justify-end gap-2 p-4 md:w-1/2 md:gap-3.5 md:p-16'>
            <PromotedBadge
                className='max-w-full whitespace-nowrap [&>*]:overflow-hidden [&>*]:text-ellipsis'
                badgeText={localizedStrings[subTitleKey]}
            />
            <Heading className='mb-0 overflow-hidden text-ellipsis whitespace-nowrap text-24 text-white md:text-44'>
                {localizedStrings[titleKey]}
            </Heading>
            {description && <p className='line-clamp-3 whitespace-pre-line text-white md:text-18'>{description}</p>}
            {buttonActionKey && (
                <div>
                    <AppButton primary dark className='mt-6 w-auto' onClick={onClick} aria-label={ariaLabel}>
                        {localizedStrings[buttonActionKey]}
                    </AppButton>
                </div>
            )}
        </div>
    );
};

export default AutoDeliveryStatusNone;
